/**
 * Tenant provisioning bootstrap (T9) — mirrors
 * backend/src/api/routes/tenants.py's POST /api/tenants/provision.
 *
 * Idempotent get-or-create: safe to call on every authenticated app load.
 * A fresh Frontegg org gets its tenant row; a replay (the tenant already
 * exists) is a no-op. Callers should still only call this once per app
 * session (see App.tsx's TenantBootstrap) — not because a second call is
 * unsafe, but because it is pointless network chatter.
 */

import { createHeaders } from './apiClient';
import { handleResponse } from './httpResponse';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * Provision the caller's own tenant. Resolves on success, on a 409
 * (existing state already provisioned/conflicting — nothing to do here),
 * and on a 401 (no session yet — do nothing rather than fail the app
 * shell over it). Any other failure throws so the caller can log it.
 */
export async function provisionTenant(): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/tenants/provision`, {
    method: 'POST',
    headers: createHeaders(),
    body: JSON.stringify({}),
  });

  if (response.ok || response.status === 409 || response.status === 401) {
    return;
  }

  // Always throws — the shared reader supplies the detail unwrapping and
  // the `.status` this call site never attached.
  await handleResponse(
    response,
    `Tenant provisioning failed: HTTP ${response.status}`
  );
}
